
import { useState, useEffect } from 'react';

interface ResearchPaper {
  id: string;
  title: string;
  author: string;
  publishDate: string;
  description: string;
  category: string;
  filePath: string;
  fileSize: string;
  downloadCount: number;
  tags: string[];
}

export default function ResearchPapers() {
  const [papers, setPapers] = useState<ResearchPaper[]>([
    {
      id: '1',
      title: '日本企業のAI導入実態調査 2024',
      author: '経済産業省研究所',
      publishDate: '2024年3月',
      description: '大企業と中小企業のAI導入格差を詳細に分析。中小企業の92%が「AIの必要性は感じるが導入方法が分からない」と回答。技術格差が企業競争力に与える影響を定量的に証明した画期的な研究。',
      category: '企業調査',
      filePath: '/documents/日本企業のAI導入実態調査2024.pdf',
      fileSize: '2.3MB',
      downloadCount: 1247,
      tags: ['AI導入', '企業調査', '競争力', '格差分析']
    },
    {
      id: '2',
      title: '世代別デジタル技術利用状況調査',
      author: '総務省統計局',
      publishDate: '2024年2月',
      description: '世代別のAI・デジタル技術利用状況を詳細に分析。世代間格差の深刻さを統計的に実証し、家族や職場でのコミュニケーション断絶の要因を明らかにした重要な調査研究。',
      category: '統計調査',
      filePath: '/documents/世代別デジタル技術利用状況調査.pdf',
      fileSize: '1.8MB',
      downloadCount: 892,
      tags: ['世代格差', 'デジタル技術', '統計分析', 'コミュニケーション']
    },
    {
      id: '3',
      title: 'Global AI Readiness Index 2024',
      author: 'MIT Technology Review',
      publishDate: '2024年1月',
      description: '日本のAI準備度が先進国中で下位に位置することが国際比較で明らか化。「AI植民地リスク」の警鐘を鳴らし、緊急の対策が必要であることを提言した重要な論文。',
      category: '国際比較',
      filePath: '/documents/Global_AI_Readiness_Index_2024.pdf',
      fileSize: '3.1MB',
      downloadCount: 2156,
      tags: ['国際比較', 'AI準備度', '植民地リスク', '政策提言']
    },
    {
      id: '4',
      title: '世代間協働学習の効果測定研究',
      author: '東京大学教育学研究科',
      publishDate: '2023年12月',
      description: '10代から80代まで、異なる世代が協働で学習する効果を科学的に検証。学習効率が単世代学習の2.3倍向上することを実証した3年間の追跡調査研究。',
      category: '教育研究',
      filePath: '/documents/世代間協働学習の効果測定研究.pdf',
      fileSize: '2.7MB',
      downloadCount: 674,
      tags: ['世代間学習', '協働学習', '効果測定', '教育効率']
    },
    {
      id: '5',
      title: 'AI時代の感覚重視教育論',
      author: '教育理論研究所',
      publishDate: '2024年3月',
      description: 'AI技術の急速な発展により、従来の知識伝達型教育では対応できない課題が顕在化。人間固有の感覚と直感を重視した新しい教育パラダイムを提案し、デジタル技術と人間の感性を融合させた学習手法を確立する。',
      category: '教育理論',
      filePath: '/documents/AI時代の感覚重視教育論.pdf',
      fileSize: '3.2MB',
      downloadCount: 1823,
      tags: ['感覚教育', '教育理論', 'AI教育', '人間性']
    }
  ]);

  const [selectedCategory, setSelectedCategory] = useState('全て');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('downloadCount');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const categories = ['全て', '企業調査', '統計調査', '国際比較', '教育研究', '教育理論'];

  const filteredPapers = papers.filter(paper => {
    const matchesCategory = selectedCategory === '全て' || paper.category === selectedCategory;
    const matchesSearch = paper.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         paper.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         paper.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const sortedPapers = [...filteredPapers].sort((a, b) => {
    switch (sortBy) {
      case 'downloadCount':
        return b.downloadCount - a.downloadCount;
      case 'title':
        return a.title.localeCompare(b.title);
      case 'publishDate':
        return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
      default:
        return 0;
    }
  });

  const handleDownload = async (paper: ResearchPaper) => {
    // ダウンロード数を増加
    const updatedPapers = papers.map(p => 
      p.id === paper.id ? { ...p, downloadCount: p.downloadCount + 1 } : p
    );
    setPapers(updatedPapers);

    // PDFビューアーページに遷移
    const encodedFilename = encodeURIComponent(paper.filePath.replace('/documents/', ''));
    if (typeof window.REACT_APP_NAVIGATE === 'function') {
      window.REACT_APP_NAVIGATE(`/pdf/${encodedFilename}`);
    } else {
      console.error('REACT_APP_NAVIGATE is not defined or not a function');
    }
  };

  const downloadAllPapers = async () => {
    for (const paper of sortedPapers) {
      try {
        const link = document.createElement('a');
        link.href = paper.filePath;
        link.download = paper.title + '.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // 少し待機してから次のダウンロード
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error(`Error downloading ${paper.title}:`, error);
      }
    }
    
    // ダウンロード数を更新
    setPapers(prev => prev.map(p => {
      if (sortedPapers.some(sp => sp.id === p.id)) {
        return { ...p, downloadCount: p.downloadCount + 1 };
      }
      return p;
    }));
  };

  const handleAdminAccess = () => {
    setShowPasswordModal(true);
    setPassword('');
    setPasswordError('');
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === '7878') {
      setShowPasswordModal(false);
      if (typeof window.REACT_APP_NAVIGATE === 'function') {
        window.REACT_APP_NAVIGATE('/admin');
      } else {
        console.error('REACT_APP_NAVIGATE is not defined or not a function');
      }
    } else {
      setPasswordError('パスワードが正しくありません');
      setPassword('');
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPassword('');
    setPasswordError('');
  };

  // SEO & Schema.org JSON-LD
  useEffect(() => {
    // Add Schema.org JSON-LD for Research Papers page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "科学的根拠に基づくAI教育研究論文",
      "description": "国内外の権威ある研究機関による最新の研究論文を無料でダウンロード。データとエビデンスが示すAI時代の現実と未来。",
      "url": `${process.env.VITE_SITE_URL || 'https://example.com'}/research-papers`,
      "mainEntity": {
        "@type": "ItemList",
        "name": "AI教育研究論文集",
        "description": "AI導入実態調査、世代格差分析、教育理論研究などの重要な論文コレクション",
        "numberOfItems": papers.length,
        "itemListElement": papers.map((paper, index) => ({
          "@type": "ScholarlyArticle",
          "position": index + 1,
          "name": paper.title,
          "author": {
            "@type": "Organization",
            "name": paper.author
          },
          "datePublished": paper.publishDate,
          "description": paper.description,
          "keywords": paper.tags.join(', '),
          "url": `${process.env.VITE_SITE_URL || 'https://example.com'}/pdf/${encodeURIComponent(paper.filePath.replace('/documents/', ''))}`
        }))
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "ホーム",
            "item": process.env.VITE_SITE_URL || 'https://example.com'
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "研究論文"
          }
        ]
      }
    });
    
    // Add SEO meta tags
    document.title = "科学的根拠に基づくAI教育研究論文 | AI育成学舎";
    
    const metaDescription = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    metaDescription.setAttribute('content', '国内外の権威ある研究機関による最新の研究論文を無料でダウンロード。AI導入実態調査、世代格差分析、教育理論研究など、データとエビデンスが示すAI時代の現実と未来をご確認ください。');
    if (!document.querySelector('meta[name="description"]')) {
      document.head.appendChild(metaDescription);
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]') || document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    metaKeywords.setAttribute('content', '研究論文,AI研究,学術論文,AI導入実態,世代格差,教育理論,無料ダウンロード,PDF');
    if (!document.querySelector('meta[name="keywords"]')) {
      document.head.appendChild(metaKeywords);
    }
    
    const canonical = document.querySelector('link[rel="canonical"]') || document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', `${process.env.VITE_SITE_URL || 'https://example.com'}/research-papers`);
    if (!document.querySelector('link[rel="canonical"]')) {
      document.head.appendChild(canonical);
    }
    
    document.head.appendChild(script);
    
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [papers]);

  return (
    <div className="min-h-screen bg-white">
      {/* ヒーローセクション */}
      <section 
        className="relative py-20 bg-cover bg-center bg-top"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/e64d6a1bd721a9583842930afedf40bd.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 via-purple-900/70 to-indigo-900/80"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center text-white">
          <h1 className="text-6xl font-bold mb-6">
            科学的根拠に基づく
            <span className="block text-5xl bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mt-4">
              AI教育研究論文
            </span>
          </h1>
          <p className="text-xl text-blue-100 max-w-4xl mx-auto leading-relaxed mb-8">
            国内外の権威ある研究機関による最新の研究論文を無料でダウンロードできます。
            データとエビデンスが示すAI時代の現実と未来をご確認ください。
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={downloadAllPapers}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:from-cyan-600 hover:to-blue-700 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap inline-flex items-center gap-3"
            >
              <i className="ri-download-cloud-line text-xl"></i>
              全論文を一括ダウンロード
            </button>
            
            <button 
              onClick={() => {
                if (typeof window.REACT_APP_NAVIGATE === 'function') {
                  window.REACT_APP_NAVIGATE('/');
                } else {
                  console.error('REACT_APP_NAVIGATE is not defined or not a function');
                }
              }}
              className="bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-white/30 transition-all border border-white/30 whitespace-nowrap inline-flex items-center gap-3"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              ホームに戻る
            </button>
          </div>
        </div>
      </section>

      {/* 検索・フィルターセクション */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-white rounded-3xl p-8 shadow-lg">
            <div className="grid md:grid-cols-3 gap-6">
              {/* 検索ボックス */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">論文を検索</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="タイトル、著者、タグで検索..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
                </div>
              </div>

              {/* カテゴリフィルター */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">カテゴリ</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {/* ソート */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">並び替え</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                >
                  <option value="downloadCount">ダウンロード数順</option>
                  <option value="title">タイトル順</option>
                  <option value="publishDate">発表日順</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 論文一覧セクション */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <h2 className="text-3xl font-bold text-gray-900">
                研究論文一覧
              </h2>
              <div className="text-gray-600">
                {sortedPapers.length} 件の論文が見つかりました
              </div>
            </div>
          </div>

          <div className="grid gap-8">
            {sortedPapers.map((paper) => (
              <div key={paper.id} className="bg-white rounded-3xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all">
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* 論文情報 */}
                  <div className="flex-1">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-6 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-text-line text-2xl text-white"></i>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                            {paper.category}
                          </span>
                          <span className="text-gray-500 text-sm">{paper.publishDate}</span>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{paper.title}</h3>
                        <p className="text-gray-600 mb-1">著者: {paper.author}</p>
                      </div>
                    </div>

                    <p className="text-gray-700 leading-relaxed mb-6">
                      {paper.description}
                    </p>

                    {/* タグ */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {paper.tags.map((tag, index) => (
                        <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* 統計情報 */}
                    <div className="flex items-center gap-6 text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <i className="ri-download-line"></i>
                        <span>{paper.downloadCount.toLocaleString()} ダウンロード</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-file-line"></i>
                        <span>{paper.fileSize}</span>
                      </div>
                    </div>
                  </div>

                  {/* ダウンロードボタン */}
                  <div className="lg:w-48 flex flex-col gap-3">
                    <button
                      onClick={() => handleDownload(paper)}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-7 text-white px-6 py-4 rounded-2xl font-bold hover:from-blue-700 hover:to-purple-800 transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-3 whitespace-nowrap"
                    >
                      <i className="ri-download-cloud-line text-xl"></i>
                      <span>ダウンロード</span>
                    </button>
                    
                    <button
                      onClick={() => navigator.clipboard.writeText(window.location.origin + paper.filePath)}
                      className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-2xl font-medium hover:bg-gray-200 transition-all flex items-center justify-center gap-3 whitespace-nowrap"
                    >
                      <i className="ri-link-line"></i>
                      <span>リンクコピー</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {sortedPapers.length === 0 && (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-search-line text-3xl text-gray-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">論文が見つかりませんでした</h3>
              <p className="text-gray-500">検索条件を変更してお試しください</p>
            </div>
          )}
        </div>
      </section>

      {/* お問い合わせセクション */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            研究論文に関するお問い合わせ
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            論文の内容についてご質問がございましたら、お気軽にお問い合わせください。
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => {
                if (typeof window.REACT_APP_NAVIGATE === 'function') {
                  window.REACT_APP_NAVIGATE('/');
                } else {
                  console.error('REACT_APP_NAVIGATE is not defined or not a function');
                }
              }}
              className="bg-gradient-to-r from-blue-600 to-purple-700 text-white px-8 py-4 rounded-full text-lg font-bold hover:from-blue-700 hover:to-purple-800 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap inline-flex items-center gap-3"
            >
              <i className="ri-mail-line text-xl"></i>
              お問い合わせ
            </button>
            
            <button 
              onClick={handleAdminAccess}
              className="bg-white text-gray-700 px-8 py-4 rounded-full text-lg font-bold hover:bg-gray-50 transition-all border border-gray-300 whitespace-nowrap inline-flex items-center gap-3"
            >
              <i className="ri-settings-line text-xl"></i>
              管理者ページ
            </button>
          </div>
        </div>
      </section>

      {/* パスワード認証モーダル */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lock-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">管理者認証</h3>
              <p className="text-gray-600">管理者ページにアクセスするにはパスワードが必要です</p>
            </div>

            <form onSubmit={handlePasswordSubmit}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  パスワード
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="パスワードを入力してください"
                  autoFocus
                />
                {passwordError && (
                  <p className="text-red-5

0 text-sm mt-2 flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    {passwordError}
                  </p>
                )}
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={closePasswordModal}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-red-500 to-orange-600 text-white px-6 py-3 rounded-lg font-medium hover:from-red-600 hover:to-orange-700 transition-all whitespace-nowrap"
                >
                  認証
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
